import { useEffect, useRef, useState } from 'react'
import { Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormGroup } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import {
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import { normalizeExpectedEntities } from './assignmentTimeline'

export type NoteSettingsValue = {
  message?: string
  ttsMessage?: string
  expectedEntities?: number
  rgb?: string
}

type Props = {
  label?: string
  message?: string
  ttsMessage?: string
  expectedEntities?: number
  rgb?: string
  messagePlaceholder: string
  ttsPlaceholder: string
  expectedEntitiesPlaceholder?: number
  rgbPlaceholder?: string
  canEdit: boolean
  useNoteLabel?: boolean
  onSave: (next: NoteSettingsValue) => void
  onOpenChange?: (open: boolean) => void
  onEditForAllReminders?: () => void
}

const AssignmentNoteSettingsDialog = ({
  label,
  message,
  ttsMessage,
  expectedEntities,
  rgb,
  messagePlaceholder,
  ttsPlaceholder,
  expectedEntitiesPlaceholder,
  rgbPlaceholder,
  canEdit,
  useNoteLabel = false,
  onSave,
  onOpenChange,
  onEditForAllReminders,
}: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [messageDraft, setMessageDraft] = useState(message ?? '')
  const [ttsMessageDraft, setTtsMessageDraft] = useState(ttsMessage ?? '')
  const [expectedEntitiesDraft, setExpectedEntitiesDraft] = useState(
    expectedEntities?.toString() ?? '',
  )
  const [rgbDraft, setRgbDraft] = useState(rgb ?? '')
  const rgbDraftRef = useRef(rgb ?? '')
  const colorPickerOpenRef = useRef(false)

  const setRgbDraftValue = (next: string) => {
    rgbDraftRef.current = next
    setRgbDraft(next)
  }

  useEffect(() => {
    if (open) return
    setMessageDraft(message ?? '')
    setTtsMessageDraft(ttsMessage ?? '')
    setExpectedEntitiesDraft(expectedEntities?.toString() ?? '')
    const nextRgb = rgb ?? ''
    rgbDraftRef.current = nextRgb
    setRgbDraft(nextRgb)
  }, [message, ttsMessage, expectedEntities, rgb, open])

  const colorValue =
    rgbDraft.trim() || rgbPlaceholder?.trim() || 'rgb(255, 255, 255)'

  const commit = () => {
    const nextMessage = messageDraft.trim()
    const nextTtsMessage = ttsMessageDraft.trim()
    const trimmedRgb = rgbDraftRef.current.trim()
    onSave({
      message: nextMessage.length ? nextMessage : undefined,
      ttsMessage: nextTtsMessage.length ? nextTtsMessage : undefined,
      expectedEntities: normalizeExpectedEntities(expectedEntitiesDraft),
      rgb: trimmedRgb.length ? trimmedRgb : undefined,
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) commit()
    setOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }

  const handleEditForAllReminders = () => {
    handleOpenChange(false)
    window.setTimeout(() => {
      onEditForAllReminders?.()
    }, 0)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='custom'
          className='h-7 w-7 p-0'
        >
          <Settings2Icon className='h-3.5 w-3.5' />
        </Button>
      </DialogTrigger>
      <DialogContent className='w-[44rem] max-w-[44rem]'>
        <DialogHeader>
          <DialogTitle>
            {label
              ? t('dashboard.team.plans.assignments.note-settings-for', {
                  label,
                })
              : t('dashboard.team.plans.assignments.note-settings')}
          </DialogTitle>
          <DialogDescription asChild>
            <div className='flex flex-wrap items-center gap-2'>
              <span>
                {t(
                  'dashboard.team.plans.assignments.note-settings-override-description',
                )}
              </span>
              {onEditForAllReminders ? (
                <Button
                  type='button'
                  variant='outline'
                  size='xs'
                  onClick={handleEditForAllReminders}
                >
                  {t('dashboard.team.plans.assignments.edit-for-all-reminders')}
                </Button>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <FormGroup>
          <div className='grow'>
            <Label>
              {t(
                useNoteLabel
                  ? 'dashboard.team.plans.assignments.note-label'
                  : 'dashboard.team.plans.assignments.note-options',
              )}
            </Label>
            <Input
              value={messageDraft}
              onChange={(event) => setMessageDraft(event.target.value)}
              onBlur={() => commit()}
              className='h-8'
              placeholder={messagePlaceholder}
              disabled={!canEdit}
            />
          </div>

          <div className='grow'>
            <Label>
              {t('dashboard.team.plans.assignments.note-tts-options')}
            </Label>
            <Input
              value={ttsMessageDraft}
              onChange={(event) => setTtsMessageDraft(event.target.value)}
              onBlur={() => commit()}
              className='h-8'
              placeholder={messageDraft.trim() || ttsPlaceholder}
              disabled={!canEdit || useNoteLabel}
            />
          </div>

          <div className='w-40'>
            <Label>
              {t('dashboard.team.plans.assignments.expected-player-count')}
            </Label>
            <Input
              type='text'
              inputMode='numeric'
              maxLength={2}
              value={expectedEntitiesDraft}
              onChange={(event) => setExpectedEntitiesDraft(event.target.value)}
              onBlur={() => commit()}
              className='h-8'
              placeholder={expectedEntitiesPlaceholder?.toString()}
              disabled={!canEdit}
            />
          </div>

          <div>
            <Label>{t('dashboard.team.plans.assignments.color')}</Label>
            <ColorPicker
              value={colorValue}
              format='rgb'
              onValueChange={(value) => {
                if (!colorPickerOpenRef.current || !canEdit) return
                if (!rgbDraft && value === colorValue) return
                setRgbDraftValue(value)
              }}
              onOpenChange={(nextOpen) => {
                colorPickerOpenRef.current = nextOpen
                if (nextOpen || !canEdit) return
                commit()
              }}
            >
              <ColorPickerTrigger asChild>
                <Button
                  variant='ghost'
                  className='p-0 h-auto'
                  disabled={!canEdit}
                >
                  <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-18.5 h-8' />
                </Button>
              </ColorPickerTrigger>
              <ColorPickerContent>
                <ColorPickerArea />
                <ColorPickerHueSlider />
                <ColorPickerInput />
              </ColorPickerContent>
            </ColorPicker>
          </div>
        </FormGroup>

        <div className='justify-end flex gap-2'>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignmentNoteSettingsDialog
